import { blockLineRange, type LineRange } from "./edit-source.js";
import { parseLsBlocks, type LsBlock } from "./parse-blocks.js";

export type ChoiceResult = "success" | "fail" | "any";
export type EffectScope = "success" | "fail" | "always";

export interface EpisodeMetaStructure {
  readonly branchKey?: string;
  readonly title?: string;
  readonly line?: number;
}

export interface CheckStructure {
  readonly line: number;
  readonly range: LineRange;
  readonly attr?: string;
  readonly dc?: string;
}

export interface StateEffectStructure {
  readonly kind: "affection" | "signal" | "butterfly" | "achievement";
  readonly line: number;
  readonly label: string;
  readonly scope: EffectScope;
  readonly routeKeys: readonly string[];
}

export interface GateRouteOutcome {
  readonly kind: "next" | "end";
  readonly value: string;
}

export interface GateRouteStructure {
  readonly index: number;
  readonly line: number;
  readonly condition?: string;
  readonly isElse: boolean;
  readonly isFallback: boolean;
  readonly outcome: GateRouteOutcome;
  readonly raw: string;
  readonly choiceRefs: readonly {
    readonly optionId: string;
    readonly result: ChoiceResult;
  }[];
}

export interface OptionRouteImpact {
  readonly result: ChoiceResult;
  readonly route: GateRouteStructure;
}

export interface StateRouteImpact {
  readonly key: string;
  readonly effect: StateEffectStructure;
  readonly route: GateRouteStructure;
}

export interface ChoiceOptionStructure {
  readonly id?: string;
  readonly mode?: "brave" | "safe";
  readonly text?: string;
  readonly line: number;
  readonly range: LineRange;
  readonly check?: CheckStructure;
  readonly effects: readonly StateEffectStructure[];
  readonly routeImpacts: readonly OptionRouteImpact[];
  readonly stateRouteImpacts: readonly StateRouteImpact[];
}

export interface ChoiceStructure {
  readonly line: number;
  readonly range: LineRange;
  readonly options: readonly ChoiceOptionStructure[];
}

export interface GateStructure {
  readonly line: number;
  readonly range: LineRange;
  readonly routes: readonly GateRouteStructure[];
  readonly hasFallback: boolean;
}

export interface EpisodeStructure {
  readonly source: string;
  readonly blocks: readonly LsBlock[];
  readonly meta: EpisodeMetaStructure;
  readonly choices: readonly ChoiceStructure[];
  readonly gate?: GateStructure;
}

function labelForChoiceRef(
  optionId: string,
  result: ChoiceResult,
  optionById: ReadonlyMap<string, ChoiceOptionStructure>,
): string {
  const option = optionById.get(optionId);
  const base = option?.text ? `「${option.text}」` : `选项 ${optionId}`;
  if (result === "success") return `${base}成功`;
  if (result === "fail") return `${base}失败`;
  return `${base}后`;
}

function humanizeStateCondition(condition: string): string {
  return condition
    .replace(/\baffection\.([A-Za-z0-9_-]+)\s*>=\s*(-?\d+)\b/g, "$1 好感达到 $2")
    .replace(/\baffection\.([A-Za-z0-9_-]+)\s*>\s*(-?\d+)\b/g, "$1 好感超过 $2")
    .replace(/\baffection\.([A-Za-z0-9_-]+)\s*<=\s*(-?\d+)\b/g, "$1 好感不超过 $2")
    .replace(/\baffection\.([A-Za-z0-9_-]+)\s*<\s*(-?\d+)\b/g, "$1 好感低于 $2")
    .replace(/\baffection\.([A-Za-z0-9_-]+)\s*={1,2}\s*(-?\d+)\b/g, "$1 好感等于 $2")
    .replace(/\s*&&\s*/g, " 且 ")
    .replace(/\s*\|\|\s*/g, " 或 ");
}

export function humanizeRouteCondition(
  route: GateRouteStructure,
  choices: readonly ChoiceStructure[] = [],
): string | undefined {
  const condition = route.condition?.trim();
  if (route.isFallback || condition?.toLowerCase() === "else") return "兜底";
  if (!condition) return undefined;

  const optionById = new Map<string, ChoiceOptionStructure>();
  for (const choice of choices) {
    for (const option of choice.options) {
      if (option.id) optionById.set(option.id, option);
    }
  }

  const withChoices = condition.replace(
    /\b([A-Za-z][A-Za-z0-9_-]*)\.(success|fail|any)\b/g,
    (match, optionId: string, result: ChoiceResult) => {
      if (!optionById.has(optionId)) return match;
      return labelForChoiceRef(optionId, result, optionById);
    },
  );

  return humanizeStateCondition(withChoices);
}

function splitLines(source: string): string[] {
  return source.split(/\r?\n/);
}

function lineAt(lines: readonly string[], line: number): string {
  return lines[line - 1] ?? "";
}

function blockRange(source: string, blocks: readonly LsBlock[], block: LsBlock): LineRange {
  const index = blocks.findIndex((candidate) => candidate.line === block.line && candidate.raw === block.raw);
  return blockLineRange(source, blocks, index);
}

function blocksInRange(blocks: readonly LsBlock[], range: LineRange): readonly LsBlock[] {
  return blocks.filter((block) => block.line > range.start && block.line < range.end);
}

function firstParam(blocks: readonly LsBlock[], key: string): string | undefined {
  const found = blocks.find((block) => block.kind === "param" && block.key === key);
  return found?.kind === "param" ? stripOuterQuotes(found.value) : undefined;
}

function stripOuterQuotes(value: string): string {
  return value.replace(/^"|"$/g, "");
}

function inlineCheck(line: string): { attr?: string; dc?: string } {
  const attr = line.match(/\battr\s*:\s*([A-Za-z0-9_]+)/)?.[1];
  const dc = line.match(/\bdc\s*:\s*(-?\d+)/)?.[1];
  return { ...(attr ? { attr } : {}), ...(dc ? { dc } : {}) };
}

function effectLabel(block: LsBlock): string | null {
  switch (block.kind) {
    case "affection":
      return [block.character, block.delta].filter(Boolean).join(" ");
    case "signal":
      if (block.signalKind === "int") {
        return [block.name, block.op, block.value].filter(Boolean).join(" ");
      }
      return ["标记", block.name].filter(Boolean).join(" ");
    case "butterfly":
      return block.description ?? "蝴蝶记忆";
    case "achievement":
      return block.id ?? "成就";
    default:
      return null;
  }
}

function effectRouteKeys(block: LsBlock): readonly string[] {
  switch (block.kind) {
    case "affection":
      return block.character ? [`affection.${block.character}`] : [];
    case "signal":
      return block.name ? [block.name] : [];
    default:
      return [];
  }
}

function effectScopeForIf(
  block: Extract<LsBlock, { kind: "if" }>,
  previousScope?: EffectScope,
): EffectScope | undefined {
  const condition = block.condition ?? "";
  if (/\bcheck\.success\b/.test(condition)) return "success";
  if (/\bcheck\.fail\b/.test(condition)) return "fail";
  if (block.isElse && !condition && previousScope === "success") return "fail";
  return undefined;
}

function collectEffects(blocks: readonly LsBlock[]): StateEffectStructure[] {
  const effects: StateEffectStructure[] = [];
  const scopeByDepth = new Map<number, EffectScope>();
  for (const block of blocks) {
    const previousScope = scopeByDepth.get(block.depth);
    for (const depth of [...scopeByDepth.keys()]) {
      if (depth >= block.depth) scopeByDepth.delete(depth);
    }
    if (block.kind === "if") {
      const scope = effectScopeForIf(block, previousScope);
      if (scope) scopeByDepth.set(block.depth, scope);
      continue;
    }
    const label = effectLabel(block);
    if (!label) continue;
    if (
      block.kind === "affection" ||
      block.kind === "signal" ||
      block.kind === "butterfly" ||
      block.kind === "achievement"
    ) {
      const scopedDepth = [...scopeByDepth.keys()]
        .filter((depth) => depth < block.depth)
        .sort((a, b) => b - a)[0];
      effects.push({
        kind: block.kind,
        line: block.line,
        label,
        scope: scopedDepth === undefined ? "always" : scopeByDepth.get(scopedDepth) ?? "always",
        routeKeys: effectRouteKeys(block),
      });
    }
  }
  return effects;
}

function parseChoiceRefs(condition: string | undefined): GateRouteStructure["choiceRefs"] {
  if (!condition) return [];
  const refs: { optionId: string; result: ChoiceResult }[] = [];
  const re = /\b([A-Za-z][A-Za-z0-9_-]*)\.(success|fail|any)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(condition)) !== null) {
    refs.push({ optionId: m[1]!, result: m[2]! as ChoiceResult });
  }
  return refs;
}

function outcomeFromText(text: string): GateRouteOutcome | undefined {
  const next = text.match(/@next\s+([A-Za-z0-9_./:-]+)/);
  if (next) return { kind: "next", value: next[1]! };
  const end = text.match(/@end\s+([A-Za-z_]+)/);
  if (end) return { kind: "end", value: end[1]! };
  return undefined;
}

function routeFromIf(block: Extract<LsBlock, { kind: "if" }>, index: number): GateRouteStructure | undefined {
  const outcome = block.next
    ? { kind: "next" as const, value: block.next }
    : block.end
      ? { kind: "end" as const, value: block.end }
      : undefined;
  if (!outcome) return undefined;
  return {
    index,
    line: block.line,
    ...(block.condition ? { condition: block.condition } : {}),
    isElse: block.isElse,
    isFallback: block.isElse && !block.condition,
    outcome,
    raw: block.raw,
    choiceRefs: parseChoiceRefs(block.condition),
  };
}

function routeFromBareLine(line: string, lineNo: number, index: number): GateRouteStructure | undefined {
  const raw = line.trim();
  const outcome = outcomeFromText(raw);
  if (!outcome) return undefined;
  const condition = raw.includes("@if") ? raw.match(/@if\s*\(([^)]*)\)/)?.[1]?.trim() : undefined;
  const isElse = /@else\b/.test(raw);
  return {
    index,
    line: lineNo,
    ...(condition ? { condition } : {}),
    isElse,
    isFallback: isElse && !condition,
    outcome,
    raw,
    choiceRefs: parseChoiceRefs(condition),
  };
}

function buildGate(source: string, blocks: readonly LsBlock[]): GateStructure | undefined {
  const gateBlocks = blocks.filter((block) => block.kind === "gate");
  const gate = gateBlocks[gateBlocks.length - 1];
  if (!gate || gate.kind !== "gate") return undefined;

  const lines = splitLines(source);
  const range = blockRange(source, blocks, gate);
  const routes: GateRouteStructure[] = [];

  if (gate.next || gate.end) {
    const outcome: GateRouteOutcome = gate.next
      ? { kind: "next", value: gate.next }
      : { kind: "end", value: gate.end! };
    routes.push({
      index: routes.length,
      line: gate.line,
      isElse: false,
      isFallback: true,
      outcome,
      raw: gate.raw,
      choiceRefs: [],
    });
  }

  for (const block of blocksInRange(blocks, range)) {
    if (block.kind === "if") {
      const route = routeFromIf(block, routes.length);
      if (route) routes.push(route);
    }
  }

  // Tolerate minimal multi-line forms that the display parser intentionally
  // does not model as route blocks: @gate { / @next main:02 / }.
  if (routes.length === 0) {
    for (let lineNo = range.start; lineNo <= range.end; lineNo++) {
      const route = routeFromBareLine(lineAt(lines, lineNo), lineNo, routes.length);
      if (route) routes.push(route);
    }
  }

  const hasFallback =
    routes.some((route) => route.isFallback) ||
    (routes.length === 1 && routes[0]?.condition === undefined);

  return { line: gate.line, range, routes, hasFallback };
}

function buildCheck(source: string, blocks: readonly LsBlock[], optionBlocks: readonly LsBlock[]): CheckStructure | undefined {
  const check = optionBlocks.find((block) => block.kind === "check");
  if (!check || check.kind !== "check") return undefined;
  const range = blockRange(source, blocks, check);
  const children = blocksInRange(blocks, range);
  const line = splitLines(source)[check.line - 1] ?? check.raw;
  const inline = inlineCheck(line);
  return {
    line: check.line,
    range,
    ...(firstParam(children, "attr") ?? inline.attr ? { attr: firstParam(children, "attr") ?? inline.attr } : {}),
    ...(firstParam(children, "dc") ?? inline.dc ? { dc: firstParam(children, "dc") ?? inline.dc } : {}),
  };
}

function impactsForOption(option: Extract<LsBlock, { kind: "option" }>, gate: GateStructure | undefined): OptionRouteImpact[] {
  if (!option.id || !gate) return [];
  const impacts: OptionRouteImpact[] = [];
  for (const route of gate.routes) {
    for (const ref of route.choiceRefs) {
      if (ref.optionId === option.id) impacts.push({ result: ref.result, route });
    }
  }
  return impacts;
}

function escapeRe(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function conditionReferencesKey(condition: string | undefined, key: string): boolean {
  if (!condition || !key) return false;
  const re = new RegExp(`(^|[^A-Za-z0-9_.-])${escapeRe(key)}([^A-Za-z0-9_.-]|$)`);
  return re.test(condition);
}

function stateImpactsForEffects(
  effects: readonly StateEffectStructure[],
  gate: GateStructure | undefined,
  optionId?: string,
): StateRouteImpact[] {
  if (!gate) return [];
  const impacts: StateRouteImpact[] = [];
  const seen = new Set<string>();
  for (const effect of effects) {
    for (const key of effect.routeKeys) {
      for (const route of gate.routes) {
        if (!conditionReferencesKey(route.condition, key)) continue;
        if (!routeCanReadEffect(route, effect, optionId)) continue;
        const id = `${effect.line}:${key}:${route.index}`;
        if (seen.has(id)) continue;
        seen.add(id);
        impacts.push({ key, effect, route });
      }
    }
  }
  return impacts;
}

function routeCanReadEffect(
  route: GateRouteStructure,
  effect: StateEffectStructure,
  optionId?: string,
): boolean {
  if (!optionId) return true;
  const refs = route.choiceRefs.filter((ref) => ref.optionId === optionId);
  if (refs.length === 0) return true;
  return refs.some((ref) => ref.result === "any" || effect.scope === "always" || ref.result === effect.scope);
}

function buildChoices(source: string, blocks: readonly LsBlock[], gate: GateStructure | undefined): ChoiceStructure[] {
  const choices: ChoiceStructure[] = [];
  for (const block of blocks) {
    if (block.kind !== "choice") continue;
    const range = blockRange(source, blocks, block);
    const childBlocks = blocksInRange(blocks, range);
    const optionBlocks = childBlocks.filter((child): child is Extract<LsBlock, { kind: "option" }> => child.kind === "option");
    const options: ChoiceOptionStructure[] = optionBlocks.map((option) => {
      const optionRange = blockRange(source, blocks, option);
      const insideOption = blocksInRange(blocks, optionRange);
      const check = buildCheck(source, blocks, insideOption);
      const effects = collectEffects(insideOption);
      return {
        line: option.line,
        range: optionRange,
        ...(option.id ? { id: option.id } : {}),
        ...(option.mode ? { mode: option.mode } : {}),
        ...(option.text !== undefined ? { text: option.text } : {}),
        ...(check ? { check } : {}),
        effects,
        routeImpacts: impactsForOption(option, gate),
        stateRouteImpacts: stateImpactsForEffects(effects, gate, option.id),
      };
    });
    choices.push({ line: block.line, range, options });
  }
  return choices;
}

export function buildEpisodeStructure(source: string): EpisodeStructure {
  const blocks = parseLsBlocks(source);
  const episode = blocks.find((block) => block.kind === "episode");
  const gate = buildGate(source, blocks);
  const choices = buildChoices(source, blocks, gate);
  const meta =
    episode?.kind === "episode"
      ? {
          ...(episode.branchKey ? { branchKey: episode.branchKey } : {}),
          ...(episode.title ? { title: episode.title } : {}),
          line: episode.line,
        }
      : {};

  return {
    source,
    blocks,
    meta,
    choices,
    ...(gate ? { gate } : {}),
  };
}
